import React, { type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { DownOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { cx } from "./fieldContext";
import { useUiText } from "./uiText";
import { ItemListItem, ItemListRoot, type ItemListPaginationConfig } from "./ItemList";
import { KEY_ARROW_DOWN, KEY_ARROW_LEFT, KEY_ARROW_RIGHT, KEY_ARROW_UP, KEY_END, KEY_HOME } from "./keyboardKeys";
const MAX_PAGES_WITHOUT_GAP = 7;
const DEFAULT_PAGE_SIZE = 10;

/*
 * Gliederungs-Bausteine des Kits (Plan 2026-10-13-antd-ausbau, Stufe 4): Tabs, Collapse, Segmented, Pagination,
 * List. Schnittstelle wie Antd im genutzten Umfang; Tastaturbedienung nach WAI-ARIA (Reiter: Pfeile, Pos1, Ende).
 */

// ---------------------------------------------------------------- Tabs

export interface TabItem {
  key: string;
  label?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  forceRender?: boolean;
  className?: string;
  style?: CSSProperties;
}

export interface TabsProps {
  items?: TabItem[];
  activeKey?: string;
  defaultActiveKey?: string;
  onChange?: (key: string) => void;
  onTabClick?: (key: string, event: React.MouseEvent) => void;
  /** Inaktive Reiter nicht im DOM halten (Antd 5: `destroyInactiveTabPane`, Antd 6: `destroyOnHidden`). */
  destroyInactiveTabPane?: boolean;
  destroyOnHidden?: boolean;
  tabBarExtraContent?: ReactNode | { left?: ReactNode; right?: ReactNode };
  size?: "small" | "middle" | "large";
  type?: "line" | "card";
  centered?: boolean;
  className?: string;
  style?: CSSProperties;
  id?: string;
}

let tabsSeed = 0;

/** Reiter (Ersatz für Antd `Tabs`); einmal geöffnete Reiter bleiben eingehängt wie bei Antd. */
export function Tabs({ items = [], activeKey, defaultActiveKey, onChange, onTabClick, destroyInactiveTabPane, destroyOnHidden, tabBarExtraContent, size, type, centered, className, style, id }: TabsProps) {
  const [innerKey, setInnerKey] = React.useState(() => defaultActiveKey ?? items.find((item) => !item.disabled)?.key ?? "");
  const current = activeKey ?? innerKey;
  const effective = items.some((item) => item.key === current) ? current : items.find((item) => !item.disabled)?.key ?? "";
  const [visited, setVisited] = React.useState<Set<string>>(() => new Set([effective]));
  if (!visited.has(effective)) setVisited(new Set(visited).add(effective));
  const baseId = React.useMemo(() => id ?? `ck-tabs-${++tabsSeed}`, [id]);
  const tabRefs = React.useRef(new Map<string, HTMLButtonElement>());
  const destroy = Boolean(destroyInactiveTabPane || destroyOnHidden);

  const select = (key: string) => {
    if (key === effective) return;
    if (activeKey === undefined) setInnerKey(key);
    onChange?.(key);
  };
  const enabled = items.filter((item) => !item.disabled);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = enabled.findIndex((item) => item.key === effective);
    let next: TabItem | undefined;
    if (event.key === KEY_ARROW_RIGHT || event.key === KEY_ARROW_DOWN) next = enabled[(index + 1) % enabled.length];
    else if (event.key === KEY_ARROW_LEFT || event.key === KEY_ARROW_UP) next = enabled[(index - 1 + enabled.length) % enabled.length];
    else if (event.key === KEY_HOME) next = enabled[0];
    else if (event.key === KEY_END) next = enabled[enabled.length - 1];
    if (!next) return;
    event.preventDefault();
    select(next.key);
    tabRefs.current.get(next.key)?.focus();
  };
  const extraLeft = tabBarExtraContent && typeof tabBarExtraContent === "object" && !React.isValidElement(tabBarExtraContent) && ("left" in tabBarExtraContent || "right" in tabBarExtraContent)
    ? (tabBarExtraContent as { left?: ReactNode }).left : undefined;
  const extraRight = tabBarExtraContent && typeof tabBarExtraContent === "object" && !React.isValidElement(tabBarExtraContent) && ("left" in tabBarExtraContent || "right" in tabBarExtraContent)
    ? (tabBarExtraContent as { right?: ReactNode }).right : (tabBarExtraContent as ReactNode);

  return (
    <div className={cx("ck-tabs", size === "small" && "ck-tabs--sm", size === "large" && "ck-tabs--lg", type === "card" && "ck-tabs--card", centered && "ck-tabs--centered", className)} style={style}>
      <div className="ck-tabs-bar">
        {extraLeft != null ? <div className="ck-tabs-extra">{extraLeft}</div> : null}
        <div className="ck-tabs-list" role="tablist" onKeyDown={onKeyDown}>
          {items.map((item) => {
            const selected = item.key === effective;
            return (
              <button
                key={item.key}
                ref={(node) => { if (node) tabRefs.current.set(item.key, node); else tabRefs.current.delete(item.key); }}
                type="button"
                role="tab"
                id={`${baseId}-tab-${item.key}`}
                aria-selected={selected}
                aria-controls={`${baseId}-panel-${item.key}`}
                tabIndex={selected ? 0 : -1}
                disabled={item.disabled}
                className={cx("ck-tabs-tab", selected && "is-active")}
                onClick={(event) => { onTabClick?.(item.key, event); select(item.key); }}
              >
                {item.icon != null ? <span className="ck-tabs-icon" aria-hidden="true">{item.icon}</span> : null}
                <span className="ck-tabs-label">{item.label}</span>
              </button>
            );
          })}
        </div>
        {extraRight != null ? <div className="ck-tabs-extra ck-tabs-extra--right">{extraRight}</div> : null}
      </div>
      {items.map((item) => {
        const selected = item.key === effective;
        const mounted = selected || item.forceRender || (!destroy && visited.has(item.key));
        if (!mounted) return null;
        return (
          <div
            key={item.key}
            role="tabpanel"
            id={`${baseId}-panel-${item.key}`}
            aria-labelledby={`${baseId}-tab-${item.key}`}
            hidden={!selected}
            className={cx("ck-tabs-panel", item.className)}
            style={item.style}
          >
            {item.children}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- Collapse

export interface CollapseItem {
  key: string | number;
  label?: ReactNode;
  children?: ReactNode;
  extra?: ReactNode;
  icon?: ReactNode;
  forceRender?: boolean;
  disabled?: boolean;
  collapsible?: "header" | "icon" | "disabled";
  showArrow?: boolean;
  className?: string;
  style?: CSSProperties;
}

export interface CollapseProps {
  items?: CollapseItem[];
  activeKey?: string | number | Array<string | number>;
  defaultActiveKey?: string | number | Array<string | number>;
  onChange?: (key: string | string[]) => void;
  accordion?: boolean;
  ghost?: boolean;
  bordered?: boolean;
  size?: "small" | "middle" | "large";
  expandIcon?: (panel: { isActive?: boolean }) => ReactNode;
  destroyInactivePanel?: boolean;
  destroyOnHidden?: boolean;
  className?: string;
  style?: CSSProperties;
}

function asKeys(value: CollapseProps["activeKey"]): string[] {
  if (value == null) return [];
  return (Array.isArray(value) ? value : [value]).map(String);
}

let collapseSeed = 0;

/** Aufklappbereiche (Ersatz für Antd `Collapse`); `onChange` liefert wie Antd ein Schlüssel-Array bzw. im Akkordeon den Schlüssel. */
export function Collapse({ items = [], activeKey, defaultActiveKey, onChange, accordion, ghost, bordered = true, size, expandIcon, destroyInactivePanel, destroyOnHidden, className, style }: CollapseProps) {
  const [innerKeys, setInnerKeys] = React.useState<string[]>(() => asKeys(defaultActiveKey));
  const keys = activeKey !== undefined ? asKeys(activeKey) : innerKeys;
  const [opened, setOpened] = React.useState<Set<string>>(() => new Set(keys));
  const baseId = React.useMemo(() => `ck-collapse-${++collapseSeed}`, []);
  const destroy = Boolean(destroyInactivePanel || destroyOnHidden);
  const toggle = (key: string) => {
    const isOpen = keys.includes(key);
    const next = accordion ? (isOpen ? [] : [key]) : isOpen ? keys.filter((k) => k !== key) : [...keys, key];
    if (activeKey === undefined) setInnerKeys(next);
    if (!isOpen) setOpened((prev) => new Set(prev).add(key));
    onChange?.(accordion ? (next[0] ?? (undefined as unknown as string)) : next);
  };
  return (
    <div className={cx("ck-collapse", ghost && "ck-collapse--ghost", !bordered && "ck-collapse--borderless", size === "small" && "ck-collapse--sm", size === "large" && "ck-collapse--lg", className)} style={style}>
      {items.map((item) => {
        const key = String(item.key);
        const active = keys.includes(key);
        const disabled = item.disabled || item.collapsible === "disabled";
        const mounted = active || item.forceRender || (!destroy && opened.has(key));
        const icon = item.showArrow === false ? null : expandIcon ? expandIcon({ isActive: active }) : <RightOutlined className="ck-collapse-arrow" />;
        return (
          <div key={key} className={cx("ck-collapse-item", active && "is-active", disabled && "is-disabled", item.className)} style={item.style}>
            <div className="ck-collapse-header">
              <button
                type="button"
                className="ck-collapse-trigger"
                aria-expanded={active}
                aria-controls={`${baseId}-${key}`}
                disabled={disabled}
                onClick={() => toggle(key)}
              >
                {icon != null ? <span className="ck-collapse-icon" aria-hidden="true">{icon}</span> : null}
                {item.icon != null ? <span className="ck-collapse-item-icon" aria-hidden="true">{item.icon}</span> : null}
                <span className="ck-collapse-label">{item.label}</span>
              </button>
              {item.extra != null ? <div className="ck-collapse-extra">{item.extra}</div> : null}
            </div>
            {mounted ? (
              <div id={`${baseId}-${key}`} className="ck-collapse-content" role="region" hidden={!active}>
                <div className="ck-collapse-body">{item.children}</div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- Segmented

export type SegmentedValue = string | number;
export type SegmentedOption<T extends SegmentedValue = SegmentedValue> = T | {
  value: T;
  label?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  title?: string;
  className?: string;
};

export interface SegmentedProps<T extends SegmentedValue = SegmentedValue> {
  options: ReadonlyArray<SegmentedOption<T>>;
  value?: T;
  defaultValue?: T;
  onChange?: (value: T) => void;
  block?: boolean;
  disabled?: boolean;
  vertical?: boolean;
  size?: "small" | "middle" | "large";
  className?: string;
  style?: CSSProperties;
  name?: string;
  "aria-label"?: string;
  id?: string;
}

/** Segmentwahl (Ersatz für Antd `Segmented`) im Kit-Bild `ck-seg`; Pfeiltasten wechseln den Wert. */
export function Segmented<T extends SegmentedValue = SegmentedValue>({ options, value, defaultValue, onChange, block, disabled, vertical, size, className, style, "aria-label": ariaLabel, id }: SegmentedProps<T>) {
  const normalized = options.map((option) => (typeof option === "object" ? option : { value: option, label: String(option) }));
  const [inner, setInner] = React.useState<T | undefined>(defaultValue ?? normalized[0]?.value);
  const current = value !== undefined ? value : inner;
  // Ein Wert außerhalb der Optionen (z. B. ein frei gewählter Zeitraum) markiert keine Stufe.
  const found = normalized.findIndex((option) => option.value === current);
  const index = Math.max(0, found);
  const refs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const choose = (next: T) => {
    if (next === current) return;
    if (value === undefined) setInner(next);
    onChange?.(next);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === KEY_ARROW_RIGHT || event.key === KEY_ARROW_DOWN ? 1 : event.key === KEY_ARROW_LEFT || event.key === KEY_ARROW_UP ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    for (let i = 1; i <= normalized.length; i++) {
      const candidate = normalized[(index + step * i + normalized.length) % normalized.length];
      if (!candidate.disabled) {
        choose(candidate.value);
        refs.current[normalized.indexOf(candidate)]?.focus();
        break;
      }
    }
  };
  const cssVars = { "--mq-kit-seg-count": normalized.length, "--mq-kit-seg-index": index } as CSSProperties;
  return (
    <div
      id={id}
      className={cx("ck-seg", "ck-segmented", size === "small" && "ck-seg--sm", block && "ck-seg--block", vertical && "ck-seg--vertical", disabled && "is-disabled", className)}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      style={{ ...cssVars, ...style }}
      onKeyDown={onKeyDown}
    >
      <span className="ck-seg-indicator" aria-hidden="true" hidden={found < 0} />
      {normalized.map((option, i) => {
        const checked = option.value === current;
        return (
          <button
            key={String(option.value)}
            ref={(node) => { refs.current[i] = node; }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked || (found < 0 && i === 0) ? 0 : -1}
            title={option.title}
            disabled={disabled || option.disabled}
            className={cx(checked && "is-active", option.className)}
            onClick={() => choose(option.value)}
          >
            {option.icon != null ? <span className="ck-seg-icon" aria-hidden="true">{option.icon}</span> : null}
            {option.label != null ? <span>{option.label}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- Pagination

export interface PaginationProps {
  current?: number;
  defaultCurrent?: number;
  total?: number;
  pageSize?: number;
  defaultPageSize?: number;
  pageSizeOptions?: Array<number | string>;
  showSizeChanger?: boolean;
  onChange?: (page: number, pageSize: number) => void;
  hideOnSinglePage?: boolean;
  showTotal?: (total: number, range: [number, number]) => ReactNode;
  size?: "small" | "default";
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
}

function pageItems(current: number, count: number): (number | "gap")[] {
  if (count <= MAX_PAGES_WITHOUT_GAP) return Array.from({ length: count }, (_, i) => i + 1);
  const pages = [...new Set([1, count, current - 1, current, current + 1])].filter((page) => page >= 1 && page <= count).sort((a, b) => a - b);
  const items: (number | "gap")[] = [];
  pages.forEach((page, index) => {
    if (index > 0 && page - pages[index - 1] > 1) items.push("gap");
    items.push(page);
  });
  return items;
}

/** Seitenwahl (Ersatz für Antd `Pagination`), optional mit Auswahl der Einträge je Seite. */
export function Pagination({ current, defaultCurrent = 1, total = 0, pageSize, defaultPageSize = 10, pageSizeOptions = [10, 20, 50, 100], showSizeChanger, onChange, hideOnSinglePage, showTotal, size, disabled, className, style }: PaginationProps) {
  const { text } = useUiText();
  const [innerPage, setInnerPage] = React.useState(defaultCurrent);
  const [innerSize, setInnerSize] = React.useState(defaultPageSize);
  const page = current ?? innerPage;
  const perPage = pageSize ?? innerSize;
  const count = Math.max(1, Math.ceil(total / perPage));
  if (hideOnSinglePage && count <= 1) return null;
  const go = (next: number, nextSize = perPage) => {
    if (current === undefined) setInnerPage(next);
    if (pageSize === undefined) setInnerSize(nextSize);
    onChange?.(next, nextSize);
  };
  const start = total === 0 ? 0 : (page - 1) * perPage + 1;
  const end = Math.min(total, page * perPage);
  return (
    <nav className={cx("ck-pagination", size === "small" && "ck-pagination--sm", className)} style={style} aria-label={text("Pages")}>
      {showTotal ? <span className="ck-pagination-total">{showTotal(total, [start, end])}</span> : null}
      <button type="button" className="ck-page-btn" aria-label={text("Previous page")} disabled={disabled || page <= 1} onClick={() => go(page - 1)}><LeftOutlined aria-hidden /></button>
      {pageItems(page, count).map((item, index) => item === "gap"
        ? <span key={`gap-${index}`} className="ck-page-gap" aria-hidden="true">…</span>
        : <button key={item} type="button" className="ck-page-btn" disabled={disabled} aria-current={item === page ? "page" : undefined} aria-label={text("Page {page}").replace("{page}", String(item))} onClick={() => go(item)}>{item}</button>)}
      <button type="button" className="ck-page-btn" aria-label={text("Next page")} disabled={disabled || page >= count} onClick={() => go(page + 1)}><RightOutlined aria-hidden /></button>
      {showSizeChanger ? (
        <span className="ck-pagination-size">
          <select className="ck-field ck-pagination-select" value={perPage} disabled={disabled} onChange={(event) => go(1, Number(event.target.value))} aria-label={text("Pages")}>
            {pageSizeOptions.map((option) => <option key={String(option)} value={Number(option)}>{option}</option>)}
          </select>
          <DownOutlined className="ck-pagination-select-icon" aria-hidden />
        </span>
      ) : null}
    </nav>
  );
}

// ---------------------------------------------------------------- List

export interface ListProps<T> {
  dataSource?: readonly T[];
  renderItem?: (item: T, index: number) => ReactNode;
  loading?: boolean;
  locale?: { emptyText?: ReactNode };
  size?: "small" | "default" | "large";
  split?: boolean;
  bordered?: boolean;
  rowKey?: string | ((item: T) => React.Key);
  itemLayout?: "horizontal" | "vertical";
  pagination?: false | { pageSize?: number; current?: number; total?: number; onChange?: (page: number, pageSize: number) => void; showSizeChanger?: boolean };
  header?: ReactNode;
  footer?: ReactNode;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

function ListRoot<T>({ dataSource = [], renderItem, loading, locale, size, split = true, bordered, itemLayout, pagination, header, footer, className, style, children }: ListProps<T>) {
  const [innerPage, setInnerPage] = React.useState(1);
  const pageSize = pagination ? pagination.pageSize ?? DEFAULT_PAGE_SIZE : 0;
  const page = pagination ? pagination.current ?? innerPage : 1;
  const visible = pageSize > 0 && pagination && pagination.total == null ? dataSource.slice((page - 1) * pageSize, page * pageSize) : dataSource;
  const pager: ItemListPaginationConfig | false = pagination && pageSize > 0
    ? { current: page, pageSize, total: pagination.total ?? dataSource.length, onChange: (next) => { setInnerPage(next); pagination.onChange?.(next, pageSize); } }
    : false;
  return (
    <div className={cx("ck-list", size === "small" && "ck-list--sm", size === "large" && "ck-list--lg", !split && "ck-list--nosplit", bordered && "ck-list--bordered", itemLayout === "vertical" && "ck-list--vertical", className)} style={style}>
      {header != null ? <div className="ck-list-header">{header}</div> : null}
      {children ?? (
        <ItemListRoot<T>
          dataSource={visible}
          renderItem={(item, index) => renderItem?.(item, index) ?? null}
          loading={loading}
          locale={locale}
          pagination={pager}
        />
      )}
      {footer != null ? <div className="ck-list-footer">{footer}</div> : null}
    </div>
  );
}

export interface ListItemProps {
  actions?: ReactNode[];
  extra?: ReactNode;
  onClick?: () => void;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

const ListItemBase = React.forwardRef<HTMLLIElement, ListItemProps>(function ListItem({ actions, extra, onClick, className, style, children }, ref) {
  return (
    <ItemListItem ref={ref} className={cx("ck-list-item", className)} style={style} onClick={onClick}>
      <div className="ck-list-item-main">{children}</div>
      {extra != null ? <div className="ck-list-item-extra">{extra}</div> : null}
      {actions && actions.length > 0 ? (
        <ul className="ck-list-item-actions">
          {actions.map((action, index) => <li key={index}>{action}</li>)}
        </ul>
      ) : null}
    </ItemListItem>
  );
});

export interface ListItemMetaProps {
  avatar?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

function ListItemMeta({ avatar, title, description, className, style }: ListItemMetaProps) {
  return (
    <div className={cx("ck-list-meta", className)} style={style}>
      {avatar != null ? <div className="ck-list-meta-avatar">{avatar}</div> : null}
      <div className="ck-list-meta-content">
        {title != null ? <div className="ck-list-meta-title">{title}</div> : null}
        {description != null ? <div className="ck-list-meta-description">{description}</div> : null}
      </div>
    </div>
  );
}

const ListItem = Object.assign(ListItemBase, { Meta: ListItemMeta });

/** Liste (Ersatz für Antd `List`) auf der Kit-Zeilenliste `ItemList`. */
export const List = Object.assign(ListRoot, { Item: ListItem });
